import React, { useEffect } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sessionsApi, userPanelApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import { Session } from 'types/Session';
import { usePair } from 'contexts/pairContext';
import { ErrorContainer } from 'components/_new/ErrorContainer/ErrorContainer';
import { PageLoading } from 'components/PageLoading';

const Sessions = () => {
	const { userPairs, currentProgramMembership } = usePanel();
	const {
		pair: { id: pairId },
	} = usePair();
	const pairUserData = userPairs?.find((pair) => pair.id === pairId);

	const {
		refetch: sessiosRefetch,
		data: sessionsData,
		isLoading,
		isError,
		error: sessionsError,
	} = useQuery({
		queryKey: ['sessions', { pairId: Number(pairId) }],
		queryFn: () => sessionsApi.getSessions(pairId, currentProgramMembership.id),
	});

	const {
		refetch: getGoalsData,
		data: goalsData,
		isLoading: isGoalsLoading,
		isError: isGoalsError,
		error: goalsError,
	} = useQuery({
		queryKey: ['goals', pairId],
		queryFn: () => userPanelApi.getGoals(pairId, currentProgramMembership.id),
		refetchOnWindowFocus: false,
	});

	useEffect(() => {
		if (!goalsData) {
			getGoalsData();
		}
	}, [goalsData, getGoalsData]);

	useEffect(() => {
		if (!sessionsData && !!pairUserData) {
			sessiosRefetch();
		}
	}, [sessionsData, pairUserData, sessiosRefetch]);

	if (!pairUserData) {
		return <ErrorContainer />;
	}

	if (isError) {
		return <ErrorContainer action={sessiosRefetch} error={sessionsError} />;
	}

	if (isGoalsError) {
		return <ErrorContainer action={getGoalsData} error={goalsError} />;
	}

	if (isLoading || isGoalsLoading) {
		return <PageLoading />;
	}

	return (
		<div className="flex-1 h-full">
			<Outlet
				context={{
					sessionsData: sessionsData || [],
					sessiosRefetch,
					isLoading,
					isGoalsLoading,
				}}
			/>
		</div>
	);
};

export default Sessions;

interface OutletContext {
	sessionsData: Session[];
	sessiosRefetch: () => void;
	isLoading: boolean;
	isGoalsLoading: boolean;
}

export function useSessionsData() {
	return useOutletContext<OutletContext>();
}
